import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import TimerIcon from '@mui/icons-material/Timer';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

type Props = {
  open: boolean;
  onClose: () => void;
};

const CodeBlock = ({ children }: { children: string }) => (
  <Box
    component="pre"
    sx={{
      bgcolor: 'grey.100',
      p: 1.5,
      borderRadius: 1,
      fontSize: '0.8rem',
      fontFamily: 'monospace',
      overflow: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      my: 1
    }}
  >
    {children}
  </Box>
);

export const ScriptHelpDialog = ({ open, onClose }: Props) => {
  const [expanded, setExpanded] = useState<string | false>('step-types');

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '85vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Script Builder Guide
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Step Types */}
        <Accordion
          expanded={expanded === 'step-types'}
          onChange={handleChange('step-types')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Step Types</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Each step in a script performs a specific action during execution.
              Use the <strong>Add Step</strong> button to add steps to setup,
              calibration, or teardown phases.
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>When to Use</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <SendIcon fontSize="small" color="primary" />
                        <strong>Command</strong>
                      </Box>
                    </TableCell>
                    <TableCell>
                      Sends an MQTT command to the station robot. Does not
                      expect a measurement result back.
                    </TableCell>
                    <TableCell>
                      Switching relays, enabling/disabling outputs, moving to a
                      position, or any device action that doesn't return a
                      reading.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <TimerIcon fontSize="small" color="primary" />
                        <strong>Wait</strong>
                      </Box>
                    </TableCell>
                    <TableCell>
                      Pauses execution for a specified duration.
                    </TableCell>
                    <TableCell>
                      Settle time after switching, warm-up delays, or any pause
                      between actions.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CameraAltIcon fontSize="small" color="primary" />
                        <strong>Get Result and Pic</strong>
                      </Box>
                    </TableCell>
                    <TableCell>
                      Sends a command to the robot and waits for a measurement
                      result response. Records the numeric value and optionally
                      captures an image from the robot's camera.
                    </TableCell>
                    <TableCell>
                      Reading a measurement value from a meter, sensor, or
                      instrument. If the robot includes an image in the
                      response, it is saved as photographic evidence.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Get Result and Pic */}
        <Accordion
          expanded={expanded === 'get-result'}
          onChange={handleChange('get-result')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Get Result and Pic</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This step type sends an MQTT command and waits for a response
              containing the measurement result. The robot publishes the result
              back on the run's measurement topic. If the response includes an
              image, it is saved as photographic evidence.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Action Field
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The <strong>Action</strong> field is the command string sent to
              the robot. The default action is:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                label="GET_READING_FROM_GPT_MINI"
                size="small"
                variant="outlined"
                icon={<CameraAltIcon />}
              />
            </Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              You can customize the action if your robot firmware uses a
              different command. The action is appended to the MQTT topic path
              for the selected station device.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Target Field
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Determines where the measurement value is recorded:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 0.5 } }}>
              <li>
                <Typography variant="body2">
                  <strong>As Found</strong> — The initial reading before any
                  adjustment. Used for the first measurement pass.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>As Left</strong> — The reading after
                  adjustment/calibration. Used for the second pass to confirm
                  the calibration.
                </Typography>
              </li>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                border: 2,
                borderColor: 'primary.main',
                borderRadius: 2,
                p: 2,
                bgcolor: 'primary.50'
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary.main"
                sx={{ mb: 1 }}
              >
                Required MQTT Response Format
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                The robot <strong>must</strong> respond on the MQTT topic using
                the action name with a <code>__COMPLETE</code> suffix followed
                by a JSON object containing the value, unit, and optionally a
                base64-encoded image.
              </Typography>

              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                With Image
              </Typography>
              <CodeBlock>
                {
                  'GET_READING_FROM_GPT_MINI__COMPLETE {"value": 10.523, "unit": "V", "image": "base64encodedstring"}'
                }
              </CodeBlock>

              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                Without Image
              </Typography>
              <CodeBlock>
                {
                  'GET_READING_FROM_GPT_MINI__COMPLETE {"value": 10.523, "unit": "V"}'
                }
              </CodeBlock>

              <Divider sx={{ my: 2 }} />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Required</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Description
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <code>__COMPLETE</code>
                      </TableCell>
                      <TableCell>Yes</TableCell>
                      <TableCell>
                        Suffix appended to the action name to indicate a
                        successful response (e.g.{' '}
                        <code>GET_READING_FROM_GPT_MINI__COMPLETE</code>).
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <code>value</code>
                      </TableCell>
                      <TableCell>Yes</TableCell>
                      <TableCell>
                        The numeric measurement reading from the device.
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <code>unit</code>
                      </TableCell>
                      <TableCell>Yes</TableCell>
                      <TableCell>
                        The unit of measurement (e.g. "V", "mV", "A", "mA",
                        "dBm", "Ω"). Common text representations like "ohm",
                        "OHM", "uA", "deg C" are automatically normalized to
                        their Unicode equivalents.
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <code>image</code>
                      </TableCell>
                      <TableCell>No</TableCell>
                      <TableCell>
                        Base64-encoded image string. If omitted or blank, no
                        image is saved — the measurement value is still
                        recorded.
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Command Step */}
        <Accordion
          expanded={expanded === 'command'}
          onChange={handleChange('command')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Command Step Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Command steps send an MQTT message to the station robot without
              expecting a measurement result. The <strong>Action</strong> field
              is the raw command string sent to the device.
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Example Actions
            </Typography>
            <CodeBlock>
              {[
                'A01 DISABLE     — Disable relay at position A01',
                'B03 ENABLE      — Enable relay at position B03',
                'set-output 5V   — Set output voltage',
                'identify         — Trigger device identification'
              ].join('\n')}
            </CodeBlock>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Dynamic Values
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Inside item scripts, you can use <code>{`{{nominal}}`}</code> in
              the action string to dynamically insert the test point's nominal
              value and function at runtime. This lets you reuse the same action
              template across test points with different values.
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Token</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Resolves To</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Example</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <code>{`{{nominal}}`}</code>
                    </TableCell>
                    <TableCell>
                      The test point's numeric value and function ID
                    </TableCell>
                    <TableCell>
                      <code>300 OHM</code>, <code>5 DCV</code>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Example
            </Typography>
            <CodeBlock>
              {[
                'Action:    CALIBRATOR_CMD:OUT {{nominal}}',
                'Resolves:  CALIBRATOR_CMD:OUT 300 OHM'
              ].join('\n')}
            </CodeBlock>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The resolved value is what gets sent over MQTT, so the device
              receives the final command (e.g.{' '}
              <code>CALIBRATOR_CMD:OUT 300 OHM</code>) and responds with{' '}
              <code>CALIBRATOR_CMD:OUT 300 OHM__COMPLETE</code>.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Topic Override
            </Typography>
            <Typography variant="body2">
              By default, commands are sent to the configured station device's
              MQTT topic. Check <strong>Override topic</strong> to send to a
              custom MQTT topic instead (advanced use).
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* QoS, Timeout, Retries */}
        <Accordion
          expanded={expanded === 'settings'}
          onChange={handleChange('settings')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              QoS, Timeout & Failure Handling
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              QoS (Quality of Service)
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Level</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Behavior</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>0</TableCell>
                    <TableCell>Fire & Forget</TableCell>
                    <TableCell>
                      Message sent once with no delivery confirmation. Fastest
                      but no guarantee of receipt. Timeout, retries, and failure
                      handling are disabled.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>1</TableCell>
                    <TableCell>At Least Once</TableCell>
                    <TableCell>
                      Message delivered at least once with acknowledgment.
                      Recommended for most steps.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>2</TableCell>
                    <TableCell>Exactly Once</TableCell>
                    <TableCell>
                      Message delivered exactly once via 4-step handshake.
                      Slowest but highest reliability.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Timeout
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              How long (in milliseconds) to wait for a response before
              considering the step failed. Default is 15,000 ms (15 seconds) for
              measurement steps and 10,000 ms (10 seconds) for commands.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Max Retries
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Number of times to retry the step if it fails or times out. Set to
              0 for no retries.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              On Failure
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Mode</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Behavior</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Abort</strong>
                    </TableCell>
                    <TableCell>
                      Stop the entire run immediately. Use for critical steps
                      where continuing doesn't make sense.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Pause</strong>
                    </TableCell>
                    <TableCell>
                      Pause the run and wait for the operator to decide. Good
                      for measurement steps where you may want to retry
                      manually.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Continue</strong>
                    </TableCell>
                    <TableCell>
                      Skip the failed step and move on. Use for non-critical
                      steps where the run should proceed regardless.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Script Structure */}
        <Accordion
          expanded={expanded === 'structure'}
          onChange={handleChange('structure')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Script Structure</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Master Script
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              A master script defines the overall test layout and execution
              flow. It contains:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 0.5 } }}>
              <li>
                <Typography variant="body2">
                  <strong>Setup Steps</strong> — Run once at the start before
                  any items are tested (e.g. initialize equipment, enable
                  power).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Rack Grid</strong> — A 4-column (A-D) x 5-row (01-05)
                  grid. Each cell can hold one or more test items.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Teardown Steps</strong> — Run once at the end after
                  all items are complete (e.g. disable outputs, reset
                  equipment).
                </Typography>
              </li>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Item Scripts
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Each item in a cell has its own script with three phases:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 0.5 } }}>
              <li>
                <Typography variant="body2">
                  <strong>Before Steps</strong> — Preparation for this specific
                  item (e.g. switch to its relay channel).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Calibration Steps</strong> — The actual measurement
                  steps (Get Result and Pic).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>After Steps</strong> — Cleanup after measuring (e.g.
                  disconnect relay).
                </Typography>
              </li>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Execution Order
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Controls the order items are processed across the rack grid:
            </Typography>
            <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
              <li>
                <Typography variant="body2">
                  <strong>Row-first</strong> — A01, B01, C01, D01, then A02,
                  B02, ... (across each row before moving down).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Column-first</strong> — A01, A02, A03, A04, A05, then
                  B01, B02, ... (down each column before moving right).
                </Typography>
              </li>
            </Box>
          </AccordionDetails>
        </Accordion>
        {/* MQTT Command Reference */}
        <Accordion
          expanded={expanded === 'mqtt-commands'}
          onChange={handleChange('mqtt-commands')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>MQTT Command Reference</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              All available MQTT commands for the robot, organized by category.
              Use these as the <strong>Action</strong> value in Command and Get
              Result steps.
            </Typography>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Session Setup &amp; Teardown
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      Command
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    [
                      'POWER_ON_STEPPERS',
                      'Turn on 24V relay → power all stepper drivers.',
                      'Run before TURN_ON_ALL_STEPPERS'
                    ],
                    [
                      'POWER_OFF_STEPPERS',
                      'Turn off relay → cut power to all stepper drivers.',
                      ''
                    ],
                    [
                      'TURN_ON_ALL_STEPPERS',
                      'Send ENABLE to all axes (A01–A16).',
                      ''
                    ],
                    [
                      'TURN_OFF_ALL_STEPPERS',
                      'Send DISABLE to all axes (A01–A16).',
                      'Last shutdown command'
                    ],
                    [
                      'HOME_ALL_STEPPERS',
                      'Home all axes in sequence. Sets mechanical zero.',
                      'Once per session. ~240s — set timeoutMs: 300000'
                    ]
                  ].map(([cmd, desc, note]) => (
                    <TableRow key={cmd}>
                      <TableCell>
                        <code>{cmd}</code>
                      </TableCell>
                      <TableCell>{desc}</TableCell>
                      <TableCell
                        sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                      >
                        {note}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              DMM Handling
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      Command
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    [
                      'DMM_FROM_{{slot}}_TO_BED',
                      'Pick up DMM from its rack slot (slot → A1–D5, resolved per run) → test bed.',
                      'Saves return position'
                    ],
                    [
                      'RETURN_DMM',
                      'Return DMM from test bed → original rack slot.',
                      ''
                    ]
                  ].map(([cmd, desc, note]) => (
                    <TableRow key={cmd}>
                      <TableCell>
                        <code>{cmd}</code>
                      </TableCell>
                      <TableCell>{desc}</TableCell>
                      <TableCell
                        sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                      >
                        {note}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              DMM Model Setup
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Run after placing the DMM on the bed. Loads socket positions,
              button map, and dial angle table.
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      Command
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    [
                      'FLUKE_87V',
                      'Load full config for Fluke 87V.',
                      'Fully implemented'
                    ],
                    [
                      'FLUKE_117',
                      'Load full config for Fluke 117.',
                      'Fully implemented'
                    ],
                    ['FLUKE_114', 'Load config for Fluke 114.', 'Placeholder'],
                    ['FLUKE_177', 'Load config for Fluke 177.', 'Placeholder'],
                    ['FLUKE_179', 'Load config for Fluke 179.', 'Placeholder']
                  ].map(([cmd, desc, note]) => (
                    <TableRow key={cmd}>
                      <TableCell>
                        <code>{cmd}</code>
                      </TableCell>
                      <TableCell>{desc}</TableCell>
                      <TableCell
                        sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                      >
                        {note}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Vision / AI
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      Command
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    [
                      'TAKE_PICTURE',
                      'Trigger camera → capture DMM image (dmm_image.jpg).',
                      ''
                    ],
                    [
                      'GET_MODEL_MANUFACTURER',
                      'Take picture + identify model + crop LCD area.',
                      ''
                    ],
                    [
                      'GET_MODEL_AND_OFFSETS',
                      'Template match to identify model + measure socket offsets.',
                      'Populates DISTANCE_TO_SOCKETS'
                    ],
                    [
                      'GET_SOCKET_POSITIONS',
                      'Alias for GET_MODEL_AND_OFFSETS.',
                      ''
                    ],
                    [
                      'TAKE_PIC__CROP__GET_GPT_RESULT',
                      'Move bed, take pic, crop LCD, send to GPT-mini → reading + unit JSON.',
                      'Primary measurement command'
                    ],
                    [
                      'GET_READING_FROM_GPT_MINI',
                      'Send cropped grayscale LCD image to GPT-mini → reading + unit JSON.',
                      'Use when image already captured'
                    ]
                  ].map(([cmd, desc, note]) => (
                    <TableRow key={cmd}>
                      <TableCell>
                        <code>{cmd}</code>
                      </TableCell>
                      <TableCell>{desc}</TableCell>
                      <TableCell
                        sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                      >
                        {note}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Button Press
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      Command
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(
                    [
                      'B1',
                      'B2',
                      'B3',
                      'B4',
                      'B5',
                      'B6',
                      'B7',
                      'B8',
                      'B9',
                      'B10'
                    ] as const
                  ).map(cmd => (
                    <TableRow key={cmd}>
                      <TableCell>
                        <code>{cmd}</code>
                      </TableCell>
                      <TableCell>
                        Press button {cmd} on the active DMM model.
                      </TableCell>
                      <TableCell
                        sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                      ></TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <code>B1_&lt;seconds&gt;</code>
                    </TableCell>
                    <TableCell>
                      Press and hold B1 for N seconds. E.g. <code>B1_5</code> =
                      hold 5s.
                    </TableCell>
                    <TableCell
                      sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                    >
                      Any Bx supports this — e.g. B3_2
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Dial Turn
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      Command
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(n => (
                    <TableRow key={n}>
                      <TableCell>
                        <code>D{n}</code>
                      </TableCell>
                      <TableCell>
                        Rotate DMM dial to position D{n} (angle set by loaded
                        model).
                      </TableCell>
                      <TableCell
                        sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                      >
                        {n >= 8 ? 'Only on models with 9+ dial positions' : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Probe Placement
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      Command
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    [
                      'PLUG_HOLD_RED_TO_A',
                      'Red probe from hold → socket A.',
                      'Touch-detect Z with retry'
                    ],
                    [
                      'PLUG_A_TO_RED_HOLD',
                      'Red probe from socket A → hold.',
                      ''
                    ],
                    [
                      'PLUG_HOLD_BLACK_TO_C',
                      'Black probe from hold → socket C.',
                      ''
                    ],
                    [
                      'PLUG_B_TO_BLACK_HOLD',
                      'Black probe from socket B → hold.',
                      ''
                    ],
                    [
                      'PLUG_HOLD_RED_TO_D',
                      'Red probe from hold → socket D.',
                      ''
                    ]
                  ].map(([cmd, desc, note]) => (
                    <TableRow key={cmd}>
                      <TableCell>
                        <code>{cmd}</code>
                      </TableCell>
                      <TableCell>{desc}</TableCell>
                      <TableCell
                        sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                      >
                        {note}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Calibrator (SCPI Serial)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Send raw SCPI commands to the Fluke calibrator over serial.
              Response published back via MQTT.
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      Command
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    [
                      'CALIBRATOR_CMD:<scpi>',
                      'Send a raw SCPI command to the calibrator.',
                      ''
                    ],
                    [
                      'CALIBRATOR_CMD:OUT <value> <unit>',
                      'Set calibrator output. E.g. CALIBRATOR_CMD:OUT 300 OHM',
                      'Use {{nominal}} token'
                    ],
                    [
                      'CALIBRATOR_CMD:OPER',
                      'Enable calibrator output.',
                      'Run after OUT'
                    ],
                    [
                      'CALIBRATOR_CMD:STBY',
                      'Calibrator standby — disable output.',
                      'Use in afterSteps'
                    ],
                    [
                      'CALIBRATOR_CMD:*IDN?',
                      'Query calibrator identity string.',
                      ''
                    ]
                  ].map(([cmd, desc, note]) => (
                    <TableRow key={cmd}>
                      <TableCell>
                        <code>{cmd}</code>
                      </TableCell>
                      <TableCell>{desc}</TableCell>
                      <TableCell
                        sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                      >
                        {note}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Raw / Advanced
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      Command
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <code>A01 GOTO=&lt;n&gt;</code>
                    </TableCell>
                    <TableCell>
                      Direct axis command — move any axis (A01–A16) to a raw
                      position. Multiple:{' '}
                      <code>A01 GOTO=500, A07 GOTO=200</code>
                    </TableCell>
                    <TableCell
                      sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                    >
                      Rarely needed. Prefer named sequences.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
    </Dialog>
  );
};
